import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../api/client";

// Нормализованный вид для фронта
export type User = {
    id: number | null;           // из БД или externalId из токена, если есть
    phone?: string | null;
    email?: string | null;
    name?: string | null;
    roles: string[];
};

type AuthState = {
    user: User | null;
    loading: boolean;
    error: string | null;
    reload: () => void;
};

const Ctx = createContext<AuthState>({
    user: null,
    loading: true,
    error: null,
    reload: () => {},
});

// Оригинальные ответы бэка (оба поддерживаем)
type MeDbUserResponse = {
    id: number;
    phone?: string | null;
    email?: string | null;
    name?: string | null;
    roles: string[];
};

type MeTokenResponse = {
    valid: true;
    phone?: string | null;
    roles?: string[];
    externalId?: number | string | null;
    payload?: { iat?: number; exp?: number };
};

function normalizeMeResponse(data: any): User | null {
    if (!data) return null;

    // Вариант A: /api/auth/me вернул сущность из БД
    const isDbShape =
        typeof data === "object" &&
        "roles" in data &&
        ("id" in data || "phone" in data || "email" in data);

    if (isDbShape) {
        const d = data as MeDbUserResponse;
        return {
            id: typeof d.id === "number" ? d.id : null,
            phone: d.phone ?? null,
            email: d.email ?? null,
            name: d.name ?? null,
            roles: Array.isArray(d.roles) ? d.roles : [],
        };
    }

    // Вариант B: /api/auth/me валидировал токен и вернул клеймы
    const isTokenShape = typeof data === "object" && data.valid === true;
    if (isTokenShape) {
        const t = data as MeTokenResponse;
        return {
            id:
                typeof t.externalId === "number"
                    ? t.externalId
                    : t.externalId != null
                    ? Number(String(t.externalId)) || null
                    : null,
            phone: t.phone ?? null,
            email: null,
            name: null,
            roles: Array.isArray(t.roles) ? t.roles : [],
        };
    }

    return null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.get<any>("/api/auth/me");
            const u = normalizeMeResponse(data);
            setUser(u);
            if (!u) setError("Не авторизован");
        } catch (e: any) {
            setUser(null);
            setError(e?.message || "Не авторизован");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Ctx.Provider value={{ user, loading, error, reload: load }}>{children}</Ctx.Provider>;
};

export const useAuth = () => useContext(Ctx);
